import { readUserSessionToken } from "@/storage/user-session"
import { controlApiFetch } from "@/transport/control-api"
import { jsonArray, jsonObject, jsonString } from "@/transport/decode"
import { expectJson, expectResponse } from "@/transport/http"
import { eventCursorReached } from "@/utils/event-cursor"
import type { PromptResponseStyle } from "@/transport/prompt"
import { normalizeRecentModels, type ModelKey, type RecentModel } from "@/utils/model-recent"
import type { ModelVisibility } from "@/utils/model-visibility"
import type { SmallModelRoute } from "@/utils/small-model-routes"

// One read of the account's preferences serves every reader until it expires:
// on a write, an account change, a failed read, or expirePreferences().
type PreferenceRead = {
  value: Promise<Record<string, unknown>>
  settled: boolean
  // The fleet event mark the control plane read the preferences at.
  events?: string
  // The mark of a stream that (re)connected while the read was in flight: the
  // read serves only if the control plane read the preferences at or after it.
  required?: string
}
let preferenceRead: PreferenceRead | undefined
let preferenceOwner = ""

function preferenceKey(path: string) {
  const key = path.split("/").filter(Boolean).at(-1)
  if (!key) throw new Error("Invalid preference path")
  return key
}

function requestPreferences(): PreferenceRead {
  const read: PreferenceRead = {
    settled: false,
    value: controlApiFetch("/api/ai/preferences", { cache: "no-store" }).then(async (response) => {
      const value = await expectJson(response, (value) => jsonObject(value, "Server returned invalid preferences"))
      read.events = response.headers.get("X-Mux-Fleet-Events") ?? undefined
      return value
    }),
  }
  read.value.then(() => { read.settled = true }, () => {
    if (preferenceRead === read) preferenceRead = undefined
  })
  return read
}

function loadPreferences(): Promise<Record<string, unknown>> {
  const owner = readUserSessionToken()
  if (preferenceOwner !== owner) {
    preferenceOwner = owner
    preferenceRead = undefined
  }
  const read = (preferenceRead ??= requestPreferences())
  return read.value.then((value) => {
    if (read.required === undefined || eventCursorReached(read.events, read.required)) return value
    // The stream may have missed a change this read predates: its readers
    // share the current read instead, a new one unless another replaced it.
    if (preferenceRead === read) preferenceRead = undefined
    return loadPreferences()
  })
}

/**
 * Later reads fetch the preferences again, for when they may have changed
 * elsewhere; readers already waiting keep the read they joined. Call it once
 * per change, before the readers it prompts: they then share one new read.
 * With `subscribed`, the fleet event mark of a stream that just (re)connected,
 * preferences the control plane read at or after that mark are kept, even
 * once a read in flight lands: the stream delivers every later change. A read
 * in flight that lands before the mark is replaced for all its readers.
 */
export function expirePreferences(subscribed?: string) {
  const read = preferenceRead
  if (!read) return
  if (subscribed === undefined) preferenceRead = undefined
  // A later reconnect's mark supersedes an earlier one: reaching it means the
  // control plane read the preferences after that stream subscribed.
  else if (!read.settled) read.required = subscribed
  else if (!eventCursorReached(read.events, subscribed)) preferenceRead = undefined
}

async function getPreference<T>(path: string, decode: (value: unknown) => T, init?: RequestInit): Promise<T> {
  const owner = readUserSessionToken()
  const preferences = await loadPreferences()
  if (init?.signal?.aborted) throw new DOMException("Aborted", "AbortError")
  if (owner !== readUserSessionToken()) throw new Error("Account changed while loading preferences")
  return decode(preferences[preferenceKey(path)])
}

function decodeModelKey(value: unknown) {
  const model = jsonObject(value, "Server returned an invalid model preference")
  jsonString(model.providerID, "Server returned an invalid model preference")
  jsonString(model.modelID, "Server returned an invalid model preference")
  if (model.variant !== undefined && model.variant !== null) jsonString(model.variant, "Server returned an invalid model preference")
  return model
}

export function getSmallModelRoutes(path: string, init?: RequestInit): Promise<SmallModelRoute[]> {
  return getPreference(path, (value) => jsonArray(value).map((item) => decodeModelKey(item) as SmallModelRoute), init)
}

/** The model desktop chats run on; null runs each on the model of the chat that starts it. */
export type DesktopModel = ModelKey & { variant?: string }

export function getDesktopModel(): Promise<DesktopModel | null> {
  return getPreference("/api/ai/preferences/desktop_model", (value) => {
    if (value === null) return null
    const model = decodeModelKey(value)
    return { providerID: model.providerID as string, modelID: model.modelID as string, ...(typeof model.variant === "string" ? { variant: model.variant } : {}) }
  })
}

function decodeModelVisibility(value: unknown) {
  return jsonArray(value).map((item) => {
    const model = decodeModelKey(item)
    if (model.visibility !== "show" && model.visibility !== "hide") throw new Error("Server returned an invalid model preference")
    if (model.favorite !== undefined && typeof model.favorite !== "boolean") throw new Error("Server returned an invalid model preference")
    if (model.updatedAt !== undefined && typeof model.updatedAt !== "number") throw new Error("Server returned an invalid model preference")
    return model as { providerID: string; modelID: string; visibility: ModelVisibility; favorite?: boolean; updatedAt?: number }
  })
}

export function getNumberPreference(path: string) {
  return getPreference(path, (value) => {
    if (typeof value !== "number" || !Number.isFinite(value)) throw new Error("Server returned an invalid preference")
    return value
  })
}

export function getNullableStringPreference(path: string) {
  return getPreference(path, (value) => value === null ? null : jsonString(value, "Server returned an invalid preference"))
}

export function getStringPreference(path: string) {
  return getPreference(path, (value) => jsonString(value, "Server returned an invalid preference"))
}

function decodeStringRecord(value: unknown) {
  const record = jsonObject(value, "Server returned an invalid preference")
  return Object.fromEntries(Object.entries(record).map(([key, value]) => [key, jsonString(value, "Server returned an invalid preference")]))
}

export function getBooleanPreference(path: string) {
  return getPreference(path, decodeBoolean)
}

function decodeBoolean(value: unknown) {
  if (typeof value !== "boolean") throw new Error("Server returned an invalid boolean preference")
  return value
}

export type ModelContextWindowDefault = ModelKey & { contextWindow: number }
/** The thinking level new chats with a model start at; an empty variant is Auto. */
export type ModelVariantDefault = ModelKey & { variant: string }
/** The speed tier every chat with a model is sent at. */
export type ModelServiceTierSetting = ModelKey & { serviceTier: string }
/** The response style every turn with a model uses. */
export type ModelResponseStyleSetting = ModelKey & { responseStyle: PromptResponseStyle }

export type ModelPreferenceChanges = {
  model_visibility?: Array<ModelKey & { visibility: ModelVisibility }>
  model_recent?: ModelKey & { variant?: string; usedAt: number }
  /** A zero contextWindow removes the model's default. */
  model_context_windows?: ModelContextWindowDefault
  /** A null variant removes the model's default. */
  model_variants?: ModelKey & { variant: string | null }
  /** An empty serviceTier removes the model's tier. */
  model_service_tiers?: ModelServiceTierSetting
  /** An empty responseStyle removes the model's style. */
  model_response_styles?: ModelKey & { responseStyle: PromptResponseStyle | "" }
  /** Display names of provider connections; an empty name restores the default. */
  provider_titles?: Record<string, string>
}

export type ModelPreferences = {
  user: ReturnType<typeof decodeModelVisibility>
  recent: RecentModel[]
  contextWindows: ModelContextWindowDefault[]
  variants: ModelVariantDefault[]
  serviceTiers: ModelServiceTierSetting[]
  responseStyles: ModelResponseStyleSetting[]
  providerTitles: Record<string, string>
}

function decodeModelContextWindow(value: unknown): ModelContextWindowDefault {
  const model = decodeModelKey(value)
  if (typeof model.contextWindow !== "number" || !Number.isInteger(model.contextWindow) || model.contextWindow <= 0) {
    throw new Error("Server returned an invalid model preference")
  }
  return { providerID: model.providerID as string, modelID: model.modelID as string, contextWindow: model.contextWindow }
}

function decodeModelVariant(value: unknown): ModelVariantDefault {
  const model = decodeModelKey(value)
  return {
    providerID: model.providerID as string, modelID: model.modelID as string,
    variant: jsonString(model.variant, "Server returned an invalid model preference"),
  }
}

function decodeModelServiceTier(value: unknown): ModelServiceTierSetting {
  const model = decodeModelKey(value)
  return {
    providerID: model.providerID as string, modelID: model.modelID as string,
    serviceTier: jsonString(model.serviceTier, "Server returned an invalid model preference"),
  }
}

function decodeModelResponseStyle(value: unknown): ModelResponseStyleSetting {
  const model = decodeModelKey(value)
  const style = model.responseStyle
  if (style !== "normal" && style !== "concise" && style !== "super-short") throw new Error("Server returned an invalid model preference")
  return { providerID: model.providerID as string, modelID: model.modelID as string, responseStyle: style }
}

/** Per-model rows; an unset preference has none. */
const modelRows = <T>(value: unknown, decode: (row: unknown) => T) => (value === undefined ? [] : jsonArray(value).map(decode))

function decodeModelPreferences(value: Record<string, unknown>): ModelPreferences {
  return {
    user: decodeModelVisibility(value.model_visibility),
    recent: normalizeRecentModels(jsonArray(value.model_recent).map(decodeModelKey)),
    contextWindows: modelRows(value.model_context_windows, decodeModelContextWindow),
    variants: modelRows(value.model_variants, decodeModelVariant),
    serviceTiers: modelRows(value.model_service_tiers, decodeModelServiceTier),
    responseStyles: modelRows(value.model_response_styles, decodeModelResponseStyle),
    providerTitles: value.provider_titles === undefined ? {} : decodeStringRecord(value.provider_titles),
  }
}

async function patchPreferences(changes: unknown) {
  const owner = readUserSessionToken()
  try {
    const value = await expectJson(await controlApiFetch("/api/ai/preferences", {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(changes),
    }), (value) => jsonObject(value, "Server returned invalid preferences"))
    if (owner !== readUserSessionToken()) throw new Error("Account changed while saving preferences")
    return value
  } finally {
    // A read sent before the write may predate it; a failed write may still have applied.
    expirePreferences()
  }
}

export async function getModelPreferences() {
  const owner = readUserSessionToken()
  const value = await loadPreferences()
  if (owner !== readUserSessionToken()) throw new Error("Account changed while loading preferences")
  return decodeModelPreferences(value)
}

export async function changeModelPreferences(changes: ModelPreferenceChanges) {
  return decodeModelPreferences(await patchPreferences(changes))
}

export async function setPreference(path: string, value: unknown) {
  const owner = readUserSessionToken()
  try {
    await expectResponse(await controlApiFetch(path, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value }),
    }), { error: "Failed to save preference" })
    if (owner !== readUserSessionToken()) throw new Error("Account changed while saving preferences")
  } finally {
    expirePreferences()
  }
}

export function getModelPresets() {
  return getPreference("/api/ai/preferences/model_presets", (value) => jsonArray(value).map((item) => {
    const model = decodeModelKey(item)
    return {
      id: jsonString(model.id, "Invalid shortcut ID"),
      providerID: model.providerID as string,
      modelID: model.modelID as string,
      keybind: jsonString(model.keybind, "Invalid shortcut keybind"),
      ...(model.variant === undefined ? {} : { variant: jsonString(model.variant, "Invalid shortcut thinking level") }),
    }
  }))
}

export function getDefaultWorkspaceSecurity() {
  return getPreference("/api/ai/preferences/default_workspace_security", (value) => {
    if (value === null) return null
    if (value === "trusted" || value === "supervised" || value === "unrestricted") return value
    throw new Error("Invalid default workspace security")
  })
}

/** Stars or unstars one project. */
export type ProjectStarChange = { project: string; starred: boolean }

/** Puts one workspace, named by its workspace key, in a project; an empty project returns it to its default project. */
export type WorkspaceProjectChange = { workspace: string; project: string }

/** Names a workspace in account state: workspace IDs are unique only on their machine. */
export function workspaceProjectKey(machine: string, workspaceID: string) {
  return `${machine}/${workspaceID}`
}

function decodeProjectNames(value: unknown) {
  return jsonArray(value, "Server returned an invalid preference").map((name) => jsonString(name, "Server returned an invalid preference"))
}

export function getProjectStars() {
  return getPreference("/api/ai/preferences/project_stars", decodeProjectNames)
}

export function getProjectOrder() {
  return getPreference("/api/ai/preferences/project_order", decodeProjectNames)
}

/** Workspace key (`workspaceProjectKey`) to the project the user put that workspace in. */
export function getWorkspaceProjects() {
  return getPreference("/api/ai/preferences/workspace_projects", decodeStringRecord)
}

/** Applies the changes in order, in one write, and returns the account's starred projects. */
export async function changeProjectStars(changes: ProjectStarChange[]) {
  return decodeProjectNames((await patchPreferences({ project_stars: changes })).project_stars)
}

/** Applies the changes in order, in one write, and returns every workspace's assigned project. */
export async function changeWorkspaceProjects(changes: WorkspaceProjectChange[]) {
  return decodeStringRecord((await patchPreferences({ workspace_projects: changes })).workspace_projects)
}
